import Phaser from "phaser";
import storyData from "../story.json";
import bg from "@/assets/game/office.webp";
import hero from "@/assets/game/man.png";

export default class NovelScene extends Phaser.Scene {
  constructor() {
    super("NovelScene");
  }

  preload() {
    this.currentSceneIndex = 0;
    this.currentLineIndex = 0;
    this.score = 0;
    this.isQuestionActive = false;
    this.isWaitingForExplain = false;
    this.load.image("bg", bg);
    this.load.image("char", hero);
  }

  create() {
    this.add.image(400, 300, "bg");
    this.add.image(400, 400, "char").setScale(0.3);

    this.textBox = this.add
      .rectangle(400, 490, 760, 200, 0x000000, 0.6)
      .setStrokeStyle(2, 0xffffff);

    // 「第〇問」表示
    this.questionLabel = this.add
      .text(50, 370, "", {
        fontFamily: "'Noto Sans JP', sans-serif",
        fontSize: "22px",
        color: "#ffff66",
        fontStyle: "bold",
        backgroundColor: "rgba(0,0,0,1.0)",
        padding: { x: 8, y: 4 },
      })
      .setDepth(2)
      .setVisible(false);

    this.text = this.add.text(40, 410, "", {
      fontFamily: "'Noto Sans JP', sans-serif",
      fontSize: "20px",
      wordWrap: { width: 730, useAdvancedWrap: true },
      color: "#ffffff",
      lineSpacing: 8,
      padding: { top: 4 },
    });

    this.nextText = this.add
      .text(700, 550, "▶", { fontSize: "20px", color: "#cccccc" })
      .setAlpha(0);

    this.nextTween = this.tweens.add({
      targets: this.nextText,
      alpha: { from: 1, to: 0 },
      ease: "Linear",
      duration: 800,
      repeat: -1,
      yoyo: true,
      paused: true,
    });

    this.clickLayer = this.add
      .rectangle(400, 300, 800, 600, 0x000000, 0)
      .setInteractive()
      .on("pointerdown", () => {
        if (this.isQuestionActive) return;
        this.advance();
      });
    this.loadScene(storyData[this.currentSceneIndex]);
  }

  // クリック誘導の表示制御
  showIndicator(show) {
    if (show) {
      this.nextText.setAlpha(1);
      this.nextTween.resume();
    } else {
      this.nextTween.pause();
      this.nextText.setAlpha(0);
    }
  }

  loadScene(sceneData) {
    this.clearUI();
    this.sceneData = sceneData;
    this.currentLineIndex = 0;
    this.isWaitingForExplain = false;

    if (sceneData.scene.startsWith("q")) {
      const num = sceneData.scene.replace("q", "");
      this.questionLabel.setText(`第${num}問`);
      this.questionLabel.setVisible(true);
    } else {
      this.questionLabel.setVisible(false);
    }

    if (sceneData.scene === "intro") {
      this.showLine(sceneData.lines[this.currentLineIndex]);
    } else {
      this.showQuestion();
    }
  }

  clearUI() {
    if (this.choicesGroup && this.choicesGroup.clear) {
      this.choicesGroup.clear(true, true);
      this.choicesGroup = null;
    }

    if (this.answerButton && this.answerButton.destroy) {
      this.answerButton.destroy();
      this.answerButton = null;
    }

    this.isQuestionActive = false;
  }

  showLine(line) {
    this.text.setText(line);
    this.showIndicator(true);
  }

  advance() {
    if (!this.sceneData) return;

    if (this.isWaitingForExplain) {
      this.isWaitingForExplain = false;
      this.showExplain();
      return;
    }

    if (this.sceneData.scene === "intro") {
      if (this.currentLineIndex < this.sceneData.lines.length - 1) {
        this.currentLineIndex++;
        this.showLine(this.sceneData.lines[this.currentLineIndex]);
      } else {
        this.currentSceneIndex++;
        this.loadScene(storyData[this.currentSceneIndex]);
      }
    } else if (this.sceneData.explainParts) {
      if (this.currentLineIndex < this.sceneData.explainParts.length - 1) {
        this.currentLineIndex++;
        this.showLine(this.sceneData.explainParts[this.currentLineIndex]);
      } else {
        this.nextScene();
      }
    }
  }

  showQuestion() {
    this.isQuestionActive = true;
    this.text.setText(this.sceneData.question);
    this.showIndicator(false);

    this.choicesGroup = this.add.group();
    let y = 450;

    this.answerButton = this.add
      .text(640, 545, "[ 回答する ]", {
        fontFamily: "'Noto Sans JP', sans-serif",
        fontSize: "20px",
        color: "#959595",
        padding: { top: 4 },
      })
      .setInteractive({ useHandCursor: false }) // クリック無効
      .on("pointerdown", () => {
        if (this.answerButton.active) this.checkAnswer();
      });

    this.answerButton.active = false; // 状態フラグ

    this.sceneData.choices.forEach((choice) => {
      const option = this.add
        .text(80, y, `□ ${choice.text}`, {
          fontFamily: "'Noto Sans JP', sans-serif",
          fontSize: "20px",
          color: "#ffffff",
          wordWrap: { width: 700 },
          padding: { top: 4 },
        })
        .setInteractive({ useHandCursor: true });

      option.isChecked = false;

      option.on("pointerdown", () => {
        option.isChecked = !option.isChecked;
        option.setText(`${option.isChecked ? "■" : "□"} ${choice.text}`);
        // 少なくとも1つ選択されているかチェック
        const anySelected = this.choicesGroup
          .getChildren()
          .some((c) => c.isChecked);

        // ボタンの有効／無効を切り替え
        if (anySelected && !this.answerButton.active) {
          this.answerButton.active = true;
          this.answerButton.setStyle({ color: "#00ffcc" });
          this.answerButton.setInteractive({ useHandCursor: true });
        } else if (!anySelected && this.answerButton.active) {
          this.answerButton.active = false;
          this.answerButton.setStyle({ color: "#959595" });
          this.answerButton.disableInteractive();
        }
      });

      this.choicesGroup.add(option);
      y += 30;
    });
  }

  checkAnswer() {
    const selected = [];
    this.choicesGroup.getChildren().forEach((opt, idx) => {
      if (opt.isChecked) selected.push(this.sceneData.choices[idx]);
    });

    const correct = this.sceneData.choices.filter((c) => c.isCorrect);
    const isAllCorrect =
      selected.length === correct.length && selected.every((s) => s.isCorrect);

    this.clearUI();

    if (isAllCorrect) {
      this.text.setText("正解だ！");
      this.score += this.sceneData.score;
    } else {
      this.text.setText("残念、不正解だ！");
    }

    this.isWaitingForExplain = true;
    this.showIndicator(true);
  }

  showExplain() {
    this.currentLineIndex = 0;
    this.text.setText(this.sceneData.explainParts[this.currentLineIndex]);
    this.showIndicator(true);
  }

  nextScene() {
    if (this.currentSceneIndex < storyData.length - 1) {
      this.currentSceneIndex++;
      this.loadScene(storyData[this.currentSceneIndex]);
    } else {
      this.scene.start("EndScene", { score: this.score });
    }
  }
}
